import './JobsPage.scss'

import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import JobPostingModal from "./modals/JobPostingModal";
import { useCurrentUser } from '../../UserProvider';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faMagnifyingGlass,
  faMusic,
  faBookmark,
  faFire,
  faEnvelope
} from "@fortawesome/free-solid-svg-icons";
import LocationInput from '../../components/Form/LocationInput';
import { GENRES, INSTRUMENTS } from "../../constants";
import classNames from 'classnames';
import JobCard from './JobCard';

import Slider from "rc-slider";
import "rc-slider/assets/index.css";

import usePageTitle from "../../hooks/PageTitle";

export default function JobsPage() {
  const { currentUser } = useCurrentUser();

  usePageTitle("Jobs");

  const [jobs, setJobs]                       = useState([]);
  const [jobToEdit, setJobToEdit]             = useState(null);
  const [page, setPage]                       = useState(1);
  const [hasMore, setHasMore]                 = useState(true);
  const [displayFilters, setDisplayFilters]   = useState(false);
  const [loading, setLoading]                 = useState(true);
  const perPage = 10;
  
  const { tab } = useParams();
  const navigate = useNavigate();
  const validTabs = ["search", "applied", "saved", "active", "posted"];
  const activeTab = validTabs.includes(tab) ? tab : "search";

  const [filters, setFilters] = useState({
    query:       "",
    location:    "",
    latitude:    null,
    longitude:   null,
    distance:    25,
    genres:      [],
    instruments: [],
    remote_only: false
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const toggleGenre = (genre) => {
    setFilters(f => {
      const newGenres = f.genres.includes(genre) ? f.genres.filter(g => g !== genre) : [...f.genres, genre];
      return { ...f, genres: newGenres };
    });
  };

  const toggleInstrument = (instrument) => {
    setFilters(f => {
      const newInst = f.instruments.includes(instrument) ? f.instruments.filter(i => i !== instrument) : [...f.instruments, instrument];
      return { ...f, instruments: newInst };
    });
  };

  const handleDistanceChange = (value) => {
    setFilters(f => ({ ...f, distance: value }));
  };

  const clearLocationValues = () => {
    setFilters(f => ({ ...f, location: "", latitude: null, longitude: null }));
  };

  const handleLocationInputClear = () => {
    clearLocationValues();
  };

  const handleLocationPlaceChange = (place) => {
    if (!place || !place.address || !place.latitude || !place.longitude) {
      return clearLocationValues();
    }

    setFilters(f => ({
      ...f,
      location: place.address,
      latitude: place.latitude,
      longitude: place.longitude
    }));
  };

  // Debounce filter changes (BUT do NOT clear jobs on first mount)
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      // Only clear results if the user actually typed a query or changed a filter
      if (filters.query.trim() !== debouncedFilters.query) {
        setJobs([]);
        setHasMore(true);
        setLoading(true);
      }
      setDebouncedFilters(filters);
    }, 800);

    return () => clearTimeout(handler);
  }, [filters, debouncedFilters]);

  // Fetch jobs on load and whenever debounced filters or page changes
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const payload = {
          per_page: perPage,
          page: page,
          query:       debouncedFilters.query,
          location:    debouncedFilters.location,
          latitude:    debouncedFilters.latitude,
          longitude:   debouncedFilters.longitude,
          distance:    debouncedFilters.distance,
          genres:      debouncedFilters.genres,
          instruments: debouncedFilters.instruments,
          remote:      debouncedFilters.remote_only ? true : null,
          applied:     activeTab === "applied",
          saved:       activeTab === "saved",
          active:      activeTab === "active",
          owned:       activeTab === "posted",
        }

        const { data } = await api.post(`/job_postings/search`, payload);
        setLoading(false);

        if (page === 1) {
          setJobs(data);
        } else {
          setJobs(prev => [...prev, ...data]);
        }

        if (data.length < perPage) {
          setHasMore(false);
        }

      } catch (err) {
        console.error('Error fetching jobs:', err);
      }
    };

    fetchJobs();
  }, [debouncedFilters, page, activeTab]);

  useEffect(() => {
    setJobs([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [activeTab]);

  const downloadAll = async () => {
    const payload = {
      query:    "",
      per_page: 500,
      page:     1,
    };

    const { data } = await api.post(`/job_postings/search`, payload);

    if (!Array.isArray(data) || data.length === 0) {
      alert("No data found.");
      return;
    }

    const headers = Object.keys(data[0]);

    const csvRows = [
      headers.join(","),
      ...data.map(item =>
        headers
          .map(header => {
            let val = item[header];
            if (val === null || val === undefined) val = "";
            val = String(val).replace(/"/g, '""');
            return `"${val}"`;
          })
          .join(",")
      ),
    ];

    const csvString = csvRows.join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute("download", "job_postings.csv");
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log("CSV downloaded!");
    console.log(data);
  };

  const reIndex = async () => {
    await api.post(`/job_postings/embed`);
    alert('Done indexing job postings');
  };

  return (
    <div className='__jobs'>
      <div className='content'>
        {currentUser?.is_admin &&
          <div className='admin-buttons'>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={reIndex}>
              Index Job Postings
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => setJobToEdit(undefined)}>
              Add New Job Posting
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => downloadAll()}>
              Download All
            </button>
          </div>
        }

        {currentUser && 
          <div className="tabs">
            <button
              className={classNames("nav-link", { active: activeTab === "search" }, { glass: activeTab === "search" })}
              onClick={() => navigate("/jobs/search")}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              Search
            </button>

            <button
              className={classNames("nav-link", { active: activeTab === "saved" }, { glass: activeTab === "saved" })}
              onClick={() => navigate("/jobs/saved")}
            >
              <FontAwesomeIcon icon={faBookmark} />
              Saved
            </button>

            <button
              className={classNames("nav-link", { active: activeTab === "applied" }, { glass: activeTab === "applied" })}
              onClick={() => navigate("/jobs/applied")}
            >
              <FontAwesomeIcon icon={faMusic} />
              Applied
            </button>

            <button
              className={classNames("nav-link", { active: activeTab === "posted" }, { glass: activeTab === "posted" })}
              onClick={() => navigate("/jobs/posted")}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Posted
            </button>

            {currentUser.is_admin &&
              <button
                className={classNames("nav-link", { active: activeTab === "active" }, { glass: activeTab === "active" })}
                onClick={() => navigate("/jobs/active")}
              >
                <FontAwesomeIcon icon={faFire} />
                Active Jobs
              </button>
            }
          </div>
        }

        <div className='main'>
          {displayFilters &&
            <div className='filters glass'>
              <div className='filters-header'>
                Filters
                <button className='btn-close' onClick={() => setDisplayFilters(false)} />
              </div>

              <label>Address</label>
              <LocationInput
                className="location"
                name="location"
                placeholder="Enter location"
                value={ filters.location }
                onInputClear={ handleLocationInputClear }
                onPlaceChange={ handleLocationPlaceChange }
              />

              <label>Range</label>
              <Slider
                min={5}
                max={100}
                value={ filters.distance }
                onChange={handleDistanceChange}
                disabled={!filters.location.trim()}
              />
              <div className='mileage'><label>{ filters.distance } miles</label></div>

          <label>Remote</label>
          <div className="remote-filter" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '8px' }}>
            <input
              type="checkbox"
              id="remote-only-checkbox"
              checked={filters.remote_only}
              onChange={(e) => setFilters(f => ({ ...f, remote_only: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="remote-only-checkbox" style={{ margin: 0, cursor: 'pointer', fontWeight: 'normal', textTransform: 'none' }}>
              Remote Jobs Only
            </label>
          </div>

              <label>Instruments</label>
              <div className="genre-grid">
                { INSTRUMENTS.map((instrument) => (
                  <div
                    key={ instrument }
                    className={ classNames("genre", { "selected": filters.instruments.includes(instrument) }) }
                    onClick={ () => toggleInstrument(instrument) }
                  >
                    { instrument }
                  </div>
                )) }
              </div>

              <label>Genre</label>
              <div className="genre-grid">
                { GENRES.map((genre) => (
                  <div
                    key={ genre }
                    className={ classNames("genre", { "selected": filters.genres.includes(genre) }) }
                    onClick={ () => toggleGenre(genre) }
                  >
                    { genre }
                  </div>
                )) }
              </div>

              <button className='mobile-filter ppy-btn ppy-btn-primary' onClick={() => setDisplayFilters(false) }>
                Filter <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          }
          <div className='results'>
            <div className='controls'>
              <button className='filter-toggle' data-cta="filter-toggle" onClick={() => setDisplayFilters(!displayFilters) }>
                <FontAwesomeIcon icon={faFilter} />
              </button>
              <input
                className='search'
                type="text"
                placeholder="Filter jobs by typing here..."
                data-cta="search-bar"
                value={filters.query}
                onChange={(e) => setFilters(f => ({ ...f, query: e.target.value }))}
              />
            </div>

            <div className='bottom-section'>
              <div className='search-results'>
                {!loading && jobs.length === 0 &&
                  <div className='empty-state'>
                    <div className='no-results'>No results found</div> 
                    <img src="/images/guy.svg" alt="Guy" />
                  </div>
                }

                {jobs.map((job) => (
                  <JobCard
                    jobId={job.id}
                    job={job}
                    setJobToEdit={setJobToEdit}
                    setJobs={setJobs}
                  />
                ))}
              </div>
            </div>

            {hasMore && jobs.length > 0 && (
              <div className="load-more">
                <button
                  className="ppy-btn ppy-btn-primary"
                  data-cta="load-more"
                  onClick={() => setPage(prev => prev + 1)}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>

        <JobPostingModal
          show={jobToEdit !== null}
          handleClose={() => setJobToEdit(null)}
          job={jobToEdit}
          setJobs={setJobs}
        />
      </div>
    </div>
  );
}
